import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { handleHashLink } from "../../lib/scrollUtils";

// Split text into words for masked reveal
const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

const HeroSection = () => {
  const ref = useRef(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center pt-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax Background */}
      <motion.div
        style={{ y: imageY, scale: imageScale }}
        className="absolute inset-0 z-0 will-change-transform"
      >
        <img
          src="https://images.pexels.com/photos/380768/pexels-photo-380768.jpeg"
          alt="Modern office interior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#F9F8F6]/85"></div>
        {/* Subtle vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9F8F6]/60 via-transparent to-[#F9F8F6]/70"></div>
      </motion.div>

      {/* Grain texture overlay */}
      <div
        className="absolute inset-0 z-[1] opacity-[0.035] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto w-full py-16 md:py-24"
      >
        <div className="max-w-4xl">
          {/* Overline with animated line */}
          <div className="flex items-center gap-4 mb-8 sm:mb-12">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              className="h-px bg-[#C85A43] w-16 sm:w-24 origin-left"
            />
            <RevealLine delay={0.4}>
              <p className="text-sm sm:text-base md:text-lg uppercase tracking-[0.3em] font-bold text-[#C85A43]">
                MARAPYAN CPA, INC.
              </p>
            </RevealLine>
          </div>

          {/* Kinetic Headline */}
          <h1
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-[6.5rem] font-semibold tracking-[-0.03em] leading-[0.95] text-[#1C1917] mb-10"
            data-testid="hero-heading"
          >
            <RevealLine delay={0.55}>
              <span>Your Partner in</span>
            </RevealLine>
            <RevealLine delay={0.72}>
              <span className="italic font-light text-[#C85A43]">
                Financial Clarity.
              </span>
            </RevealLine>
          </h1>

          {/* Body */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl"
            data-testid="hero-subheading"
          >
            <p className="text-base sm:text-lg leading-relaxed font-semibold text-[#1C1917] mb-4">
              A smarter way to manage taxes, accounting, and financial growth.
              We are a fully virtual CPA firm based in Burbank, California,
              serving Los Angeles and clients across the state—built for
              entrepreneurs, professionals, and growing businesses in
              today's digital economy.
            </p>
            <p className="text-sm sm:text-base leading-relaxed font-medium text-[#3A3634]">
              Strategic tax planning · tax preparation and filing · full-service bookkeeping · financial statement preparation. All delivered directly to your screen.
            </p>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <Link
              to="/#contact"
              onClick={(e) => handleHashLink(e, "contact", location.pathname)}
              className="group inline-flex items-center gap-3 bg-[#1C1917] hover:bg-[#C85A43] text-white rounded-full pl-7 pr-2 py-2 text-sm sm:text-base font-medium tracking-wide transition-all duration-500"
              data-testid="hero-cta-primary"
            >
              <span>Schedule a Consultation</span>
              <span className="w-10 h-10 rounded-full bg-[#C85A43] group-hover:bg-white flex items-center justify-center transition-all duration-500">
                <ArrowUpRight className="w-4 h-4 text-white group-hover:text-[#C85A43] transition-colors duration-500" />
              </span>
            </Link>
            <Link
              to="/#services"
              onClick={(e) => handleHashLink(e, "services", location.pathname)}
              className="inline-flex items-center gap-2 px-6 py-4 text-sm sm:text-base font-medium text-[#1C1917] hover:text-[#C85A43] transition-colors border-b border-[#1C1917]/20 hover:border-[#C85A43] self-start"
              data-testid="hero-cta-secondary"
            >
              Explore our services
            </Link>
          </motion.div>
        </div>
      </motion.div>

      {/* Corner meta - top right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute top-28 right-6 md:right-12 lg:right-24 z-10 hidden md:block text-right"
      >
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#78716C] mb-2">
          Est. 2026 · Burbank, CA
        </p>
        <p className="font-heading text-sm text-[#1C1917] italic">
          Licensed CPA · MSA
        </p>
      </motion.div>

      {/* Scroll cue - bottom right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 right-6 md:right-12 lg:right-24 z-10 flex items-center gap-3"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#78716C]">
          Scroll
        </span>
        <div className="w-16 h-px bg-[#78716C]/40 relative overflow-hidden">
          <motion.div
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 bg-[#C85A43]"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
